import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Data availability helpers (neutral terminology, NOT risk-based)
export const getDataStatusClass = (reviewCount) => {
    if (reviewCount >= 3) return 'data-available';
    return 'data-insufficient';
};

export const getDataStatusColor = (reviewCount) => {
    if (reviewCount >= 3) return 'text-primary';
    return 'text-muted-foreground';
};

export const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
};

export const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

export const getProjectSizeLabel = (size) => {
    const sizes = {
        small: 'Small Project',
        medium: 'Medium Project',
        large: 'Large Project'
    };
    return sizes[size] || size;
};

export const CONTRACTOR_TRADES = [
    'General Contractor',
    'Electrician',
    'Plumber',
    'HVAC',
    'Roofing',
    'Painting',
    'Landscaping',
    'Flooring',
    'Carpentry',
    'Masonry',
    'Drywall',
    'Siding',
    'Windows & Doors',
    'Kitchen & Bath',
    'Pool & Spa',
    'Other'
];

export const JOB_TYPES = [
    'New Construction',
    'Renovation',
    'Repair',
    'Maintenance',
    'Installation',
    'Inspection',
    'Consultation',
    'Emergency Service',
    'Other'
];

export const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w ago`;
    
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    
    const years = Math.floor(days / 365);
    return `${years}y ago`;
};

/**
 * Extract a human-readable error message from various error formats.
 * Handles:
 * - String errors
 * - Pydantic/FastAPI validation errors (array of {msg, loc, type})
 * - Axios error responses
 * - Generic error objects
 * 
 * @param {any} error - The error to extract message from
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} Human-readable error message
 */
export const getErrorMessage = (error, fallback = 'An unexpected error occurred') => {
    // Already a string
    if (typeof error === 'string') {
        return error;
    }
    
    // Null/undefined
    if (!error) {
        return fallback;
    }
    
    // Axios error with response data
    if (error.response?.data) {
        const detail = error.response.data.detail;
        
        // FastAPI/Pydantic validation error array
        if (Array.isArray(detail)) {
            // Extract messages from validation errors
            const messages = detail.map(err => {
                if (typeof err === 'string') return err;
                if (err.msg) {
                    // Format field location if available
                    const field = err.loc?.slice(-1)[0];
                    if (field && field !== 'body') {
                        return `${field}: ${err.msg}`;
                    }
                    return err.msg;
                }
                return null;
            }).filter(Boolean);
            
            if (messages.length > 0) {
                return messages.length === 1 
                    ? messages[0] 
                    : messages.slice(0, 3).join('. ');
            }
        }
        
        // String detail
        if (typeof detail === 'string') {
            return detail;
        }
        
        // Object with message
        if (detail?.message) {
            return detail.message;
        }
        if (detail?.msg) {
            return detail.msg;
        }
        
        // Try response.data.message directly
        if (error.response.data.message) {
            return error.response.data.message;
        }
    }
    
    // Standard Error object
    if (error.message) {
        return error.message;
    }
    
    // Object with msg property
    if (error.msg) {
        return error.msg;
    }
    
    // Array of errors
    if (Array.isArray(error)) {
        const messages = error.map(e => 
            typeof e === 'string' ? e : e?.msg || e?.message
        ).filter(Boolean);
        if (messages.length > 0) {
            return messages.slice(0, 3).join('. ');
        }
    }
    
    // Last resort - try to stringify
    try {
        const str = JSON.stringify(error);
        if (str && str !== '{}' && str.length < 200) {
            return str;
        }
    } catch {
        // Ignore stringify errors
    }
    
    return fallback;
};
